import React, { useEffect, useMemo, useState } from 'react';
import { ListFilter as FilterIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from 'src/shared/components/ui/popover';
import { Button } from 'src/shared/components/ui/button';
import type { SelectedFilters } from 'src/app/portfolio/state/portfolioGeneralSlice';
import type { SiteFilterOptions } from 'src/app/portfolio/state/api/siteFilterOptionsQuery';
import { useGetRiskInsightsSitesQuery } from 'src/app/portfolio/state/api/riskInsightsEngineSlice';
import { sanitizeSelectedFilters } from 'src/app/portfolio/utils/sanitizeGnrFilters';
import { siteEstateFiltersOf } from 'src/app/portfolio/views/global-nature-risk/siteEstateFilters';
import { useAnimatedCount } from './useAnimatedCount';
import ActivitiesFilterAccordion from './ActivitiesFilterAccordion';
import CountryFilterAccordion from './CountryFilterAccordion';
import RiskDimensionAccordion, { SON_LEVELS } from './RiskDimensionAccordion';

const ISIC_KEY = 'isic';
const COUNTRY_KEY = 'country';
const PHYSICAL_RISK_KEY = 'physical_risk_levels';
const DEPENDENCY_KEY = 'dependency_levels';
const IMPACT_KEY = 'impact_levels';
const SON_KEY = 'son_levels';

type Props = {
    selectedFilters: SelectedFilters;
    onApply: (filters: SelectedFilters) => void;
    onClear: () => void;
    filterOptions?: SiteFilterOptions;
    isLoadingFilterOptions?: boolean;
    businessCentralId?: string;
};

const valuesFor = (filters: SelectedFilters, key: string) => filters.find(f => f.name === key)?.values ?? [];

const setFilter = (filters: SelectedFilters, key: string, values: string[]): SelectedFilters => {
    const without = filters.filter(f => f.name !== key);
    return values.length > 0 ? [...without, { name: key, values }] : without;
};

const SiteListFilters = ({ selectedFilters, onApply, onClear, filterOptions, isLoadingFilterOptions, businessCentralId }: Props) => {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState<SelectedFilters>(selectedFilters);

    useEffect(() => {
        if (open) {
            setDraft(selectedFilters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const sanitizedDraft = useMemo(() => sanitizeSelectedFilters(draft, filterOptions), [draft, filterOptions]);

    const { data: countData } = useGetRiskInsightsSitesQuery(
        {
            businessCentralId: businessCentralId!,
            page: 1,
            pageSize: 1,
            ...siteEstateFiltersOf({ selectedFilters: sanitizedDraft }),
        },
        { skip: !open || !businessCentralId }
    );

    const totalItems = countData?.organisation?.sites?.pagination.totalItems;
    const animatedCount = useAnimatedCount(totalItems);

    const applyLabel = !businessCentralId ? 'Apply' : animatedCount === undefined ? 'View ... sites' : `View ${animatedCount} sites`;

    const update = (key: string, values: string[]) => {
        setDraft(prev => setFilter(prev, key, values));
    };

    const handleApply = () => {
        onApply(draft);
        setOpen(false);
    };

    const handleClear = () => {
        setDraft([]);
        onClear();
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant='outline' aria-label='Filter Sites' data-testid='site-list-filter-button'>
                    <FilterIcon width={16} height={16} />
                    Filter Sites
                    {selectedFilters.length > 0 && <> ({selectedFilters.reduce((sum, f) => sum + f.values.length, 0)})</>}
                </Button>
            </PopoverTrigger>
            <PopoverContent align='end' className='w-[350px] flex flex-col p-0 max-h-[80vh]'>
                <div className='flex flex-row items-center justify-between px-5 py-3 border-b shrink-0'>
                    <span className='text-xl'>Filter Sites</span>
                    <Button variant='ghost' size='sm' onClick={handleClear} aria-label='Clear site filters'>
                        Clear
                    </Button>
                </div>

                <div className='flex-1 overflow-y-auto px-5 py-2 flex flex-col gap-0'>
                    <ActivitiesFilterAccordion
                        isicCodes={filterOptions?.isicCodes ?? []}
                        isLoading={isLoadingFilterOptions ?? false}
                        selectedValues={valuesFor(draft, ISIC_KEY)}
                        onChange={values => update(ISIC_KEY, values)}
                    />
                    <CountryFilterAccordion
                        countries={filterOptions?.countries ?? []}
                        isLoading={isLoadingFilterOptions ?? false}
                        selectedValues={valuesFor(draft, COUNTRY_KEY)}
                        onChange={values => update(COUNTRY_KEY, values)}
                    />
                    <RiskDimensionAccordion
                        label='Physical Risk'
                        selectedValues={valuesFor(draft, PHYSICAL_RISK_KEY)}
                        onChange={values => update(PHYSICAL_RISK_KEY, values)}
                    />
                    <RiskDimensionAccordion
                        label='Dependencies'
                        selectedValues={valuesFor(draft, DEPENDENCY_KEY)}
                        onChange={values => update(DEPENDENCY_KEY, values)}
                    />
                    <RiskDimensionAccordion
                        label='Impact on Nature'
                        selectedValues={valuesFor(draft, IMPACT_KEY)}
                        onChange={values => update(IMPACT_KEY, values)}
                    />
                    <RiskDimensionAccordion
                        label='State of Nature'
                        selectedValues={valuesFor(draft, SON_KEY)}
                        onChange={values => update(SON_KEY, values)}
                        levels={SON_LEVELS}
                    />
                </div>

                <div className='flex flex-row gap-2 px-4 py-3 border-t shrink-0'>
                    <Button variant='outline' className='flex-grow-0' onClick={() => setOpen(false)} aria-label='Cancel'>
                        Cancel
                    </Button>
                    <Button className='flex-1' onClick={handleApply} data-testid='apply-filters-button'>
                        {applyLabel}
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default SiteListFilters;
